import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import ReportsService from '../../../services/reports.service';
import SearchableDropdown from '../../common/SearchableDropdown';
import { DATE_PRESETS, getPresetRange } from '../Common/datePresets';
import { matchingReservations, metrics, metricValue } from './helpers';
import './styles.css';

const PAGE_SIZE = 25;
function ReportTable({ columns, rows, empty = 'No results for these filters.' }) {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [rows]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pages - 1);
  return <>
    <div className="trip-report-table-wrap">
      <table className="uk-table uk-table-divider hms-table">
        <thead><tr>{columns.map(column => <th key={column.key} scope="col">{column.label}</th>)}</tr></thead>
        <tbody>{rows.length ? rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE).map((row, index) => <tr key={row.key || row.guest_reservation_id || row.rental_id || row.source || index}>
          {columns.map(column => <td key={column.key}>{column.render ? column.render(row) : row[column.key]}</td>)}
        </tr>) : <tr><td colSpan={columns.length}>{empty}</td></tr>}</tbody>
      </table>
    </div>
    {rows.length > PAGE_SIZE && <nav className="trip-report-pagination" aria-label="Table pagination">
      <button type="button" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Previous</button>
      <span>Page {safePage + 1} of {pages} · {rows.length} records</span>
      <button type="button" disabled={safePage + 1 === pages} onClick={() => setPage(safePage + 1)}>Next</button>
    </nav>}
  </>;
}

export default function TripPlanning() {
  const [draft, setDraft] = useState(() => ({ start_date: moment().subtract(7, 'days').format('YYYY-MM-DD'), end_date: moment().format('YYYY-MM-DD'), rental_id: '', destination_id: '' }));
  const [filters, setFilters] = useState(draft);
  const [preset, setPreset] = useState('last-7');
  const [report, setReport] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [section, setSection] = useState('overview');
  const [drilldown, setDrilldown] = useState({ mode: 'all' });
  const [itinerary, setItinerary] = useState(null);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    setReport(null);
    setItinerary(null);
    setDrilldown({ mode: 'all' });
    ReportsService.getTripPlanning(filters).then(response => {
      if (!current) return;
      setReport(response.data);
      setOptions(response.data.options);
    }).catch(() => {
      if (current) setError('Unable to load the Trip Planning report. Please try again.');
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [filters, attempt]);

  const destinations = useMemo(() => [...new Map(options.map(row => [row.destination_id, row.destination_name])).entries()], [options]);
  const reservations = useMemo(() => matchingReservations(report?.reservations || [], drilldown), [report, drilldown]);
  const pending = Object.keys(filters).some(key => filters[key] !== draft[key]);
  const update = event => {
    const { name, value } = event.target;
    setPreset('custom');
    setDraft(previous => ({ ...previous, [name]: value, ...(name === 'destination_id' ? { rental_id: '' } : {}) }));
  };
  const changePreset = event => {
    const value = event.target.value;
    setPreset(value);
    const range = getPresetRange(value);
    if (!range) return;
    const next = { ...draft, start_date: range[0].format('YYYY-MM-DD'), end_date: range[1].format('YYYY-MM-DD') };
    setDraft(next);
    setFilters(next);
  };

  const datesValid = moment(draft.start_date, 'YYYY-MM-DD', true).isValid()
    && moment(draft.end_date, 'YYYY-MM-DD', true).isValid()
    && draft.start_date <= draft.end_date
    && moment(draft.end_date).diff(moment(draft.start_date), 'days') <= 365;
  const hasDrilldown = drilldown.mode !== 'all' || Boolean(drilldown.rental || drilldown.place || drilldown.source);

  useEffect(() => {
    if (!pending || !datesValid) return;
    const datesChanged = draft.start_date !== filters.start_date || draft.end_date !== filters.end_date;
    if (!datesChanged) {
      setFilters(draft);
      return;
    }
    const timer = setTimeout(() => setFilters(draft), 350);
    return () => clearTimeout(timer);
  }, [draft, filters, pending, datesValid]);

  const details = selection => {
    setDrilldown(selection);
    setItinerary(null);
    setSection('reservations');
  };
  const showItinerary = row => {
    setItinerary(row);
    setTimeout(() => document.getElementById('trip-report-itinerary')?.focus(), 0);
  };
  const drillLabel = drilldown.label || ({ all: 'Reservations', planned: 'Reservations with saved plans', shared: 'Planned trips with sharing enabled' }[drilldown.mode]);

  return <section className="reports-tab-content trip-report" aria-labelledby="trip-report-title">
    <h2 id="trip-report-title">Trip Planning</h2>
    <form className="trip-report-filters" onSubmit={event => event.preventDefault()}>
      <div className="uk-form-controls trip-report-date-preset">
        <select className="uk-select" aria-label="Date range" value={preset} onChange={changePreset}>
          {DATE_PRESETS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>
      <div className="uk-form-controls trip-report-date-field"><span className="hms-date-picker"><input className="uk-input" type="date" name="start_date" aria-label="Check-in from" required value={draft.start_date} onChange={update} /></span></div>
      <div className="uk-form-controls trip-report-date-field"><span className="hms-date-picker"><input className="uk-input" type="date" name="end_date" aria-label="Check-in through" required value={draft.end_date} onChange={update} /></span></div>
      <div className="trip-report-filter-field" role="group" aria-label="Destination">
        <div className="uk-form-controls">
          <SearchableDropdown
            inline
            value={draft.destination_id}
            onChange={value => setDraft(previous => ({ ...previous, destination_id: value, rental_id: '' }))}
            placeholder="All Destinations"
            options={[{ value: '', label: 'All Destinations' }, ...destinations.map(([value, label]) => ({ value, label }))]}
          />
        </div>
      </div>
      <div className="trip-report-filter-field" role="group" aria-label="Property">
        <div className="uk-form-controls">
          <SearchableDropdown
            inline
            value={draft.rental_id}
            onChange={value => setDraft(previous => ({ ...previous, rental_id: value }))}
            placeholder="All Properties"
            options={[
              { value: '', label: 'All Properties' },
              ...options.filter(row => !draft.destination_id || row.destination_id === draft.destination_id)
                .map(row => ({ value: row.rental_id, label: row.rental_name })),
            ]}
          />
        </div>
      </div>
    </form>
    {!datesValid && <p role="alert">Choose a valid check-in date range of at most 366 days.</p>}
    {pending && datesValid && <p role="status">Updating filters…</p>}
    {error && <div className="uk-alert-danger" role="alert">{error} {!report && <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry</button>}</div>}
    {loading && <p role="status">Loading Trip Planning report…</p>}
    {report && !loading && <>
      <div className="trip-report-toolbar">
        <div className="rbr-pill-toggle" role="group" aria-label="Report sections">{[['overview', 'Overview'], ['places', 'Popular Places'], ['reservations', 'Reservation Details']].map(([key, label]) => <button key={key} type="button" className={`rbr-pill-toggle-button${section === key ? " is-active" : ""}`} aria-pressed={section === key} onClick={() => { setSection(key); setItinerary(null); }}>{label}</button>)}</div>
      </div>
      {section === 'overview' && <>
        <div className="trip-report-metrics">{metrics.filter(([key]) => key !== 'sharing_enabled').map(([key, label, mode]) => <button key={key} type="button" className="overview-metric-card trip-report-metric" onClick={() => details({ mode })}>
          <span className="metric-header">
            <span className="metric-title">{label}</span>
            <span className="metric-total">{metricValue(key, report.summary[key])}</span>
          </span>
        </button>)}</div>
        <p className="trip-report-note">Adoption is the share of reservations with at least one saved activity. Only reservations with a linked guestbook are included. Sharing enabled means an active link, not a confirmed view.</p>
        <h3>Property comparison</h3>
        <ReportTable rows={report.properties} columns={[
          { key: 'rental_name', label: 'Property', render: row => <button className="trip-report-link" onClick={() => details({ mode: 'all', rental: row.rental_id, label: row.rental_name })}>{row.rental_name}</button> },
          ...metrics.filter(([key]) => key !== 'saved_activities').map(([key, label]) => ({ key, label, render: row => metricValue(key, row[key]) })),
        ]} />
      </>}
      {section === 'places' && <>
        <h3>Most-saved places</h3>
        <p className="trip-report-note">Ranked by distinct reservations. These are planning choices, not confirmed visits. Custom activities appear in the activity mix below.</p>
        <ReportTable rows={report.places} columns={[
          { key: 'name', label: 'Place', render: row => <button className="trip-report-link" onClick={() => details({ mode: 'all', place: row.key, label: row.name })}>{row.name}</button> },
          { key: 'category', label: 'Category' }, { key: 'sources', label: 'Source' },
          { key: 'distinct_reservations', label: 'Reservations' }, { key: 'saved_activities', label: 'Saved activities' },
        ]} />
        <h3>Activity mix</h3>
        <ReportTable rows={report.activity_mix} columns={[
          { key: 'source', label: 'Activity type', render: row => <button className="trip-report-link" onClick={() => details({ mode: 'all', source: row.source, label: row.source })}>{row.source}</button> },
          { key: 'distinct_reservations', label: 'Reservations' }, { key: 'saved_activities', label: 'Saved activities' },
        ]} />
        <p className="trip-report-note">Activity types do not indicate whether an item was added manually or through AI.</p>
      </>}
      {section === 'reservations' && <>
        <div className="trip-report-toolbar trip-report-reservations-heading"><h3>{drillLabel} ({reservations.length})</h3>{hasDrilldown && <button type="button" className="uk-button uk-button-default" onClick={() => { setDrilldown({ mode: 'all' }); setItinerary(null); }}>Clear drill-down</button>}</div>
        <ReportTable rows={reservations} columns={[
          { key: 'reservation_id', label: 'Reservation', render: row => row.reservation_id || row.guest_reservation_id },
          { key: 'rental_name', label: 'Property' }, { key: 'check_in', label: 'Check-in' }, { key: 'check_out', label: 'Check-out' },
          { key: 'activities', label: 'Saved activities', render: row => row.items.length },
          { key: 'sharing_enabled', label: 'Sharing enabled', render: row => row.sharing_enabled ? 'Yes' : 'No' },
          { key: 'itinerary', label: 'Itinerary', render: row => row.items.length ? <a href="#trip-report-itinerary" onClick={event => { event.preventDefault(); showItinerary(row); }}>View itinerary</a> : 'No saved activities' },
        ]} />
        {itinerary && <section id="trip-report-itinerary" tabIndex={-1} className="trip-report-itinerary" aria-label="Read-only itinerary">
          <div className="trip-report-toolbar"><h3>Itinerary · {itinerary.reservation_id || itinerary.guest_reservation_id}</h3><button type="button" onClick={() => setItinerary(null)}>Close itinerary</button></div>
          <p>{itinerary.rental_name} · {itinerary.check_in}–{itinerary.check_out} · Read-only</p>
          <ReportTable rows={itinerary.items} columns={[{ key: 'date', label: 'Date' }, { key: 'time', label: 'Time', render: row => row.time?.slice(0, 5) || 'Unscheduled' }, { key: 'title', label: 'Activity' }, { key: 'source', label: 'Source' }]} />
        </section>}
      </>}
      <footer className="secondary">See how reservations use trip planning and which places guests save. Counts reflect activities saved now for each period, not historical snapshots. Preview sessions and canceled reservations are excluded.</footer>
    </>}
  </section>;
}
